import React, { useEffect, useState } from 'react';
import { Box, Chip, TextField, InputAdornment, FormControl, Select, MenuItem, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import ConfirmDialog from './ui/ConfirmDialog';
import { useToast } from './ui/ToastProvider';
import { VALID_MTOS, VALID_SUB_AGENTS, type ValidMTO, type ValidSubAgent } from '../utils/constants';
import { transactionStore } from '../utils/transactionStore';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
import { MOCK_FAILED_TRANSACTIONS } from './FailedTransactionsTable';
// Types
type PayoutType = 'FT' | 'IBFT' | 'OTC';
type LifecycleStatus = 'IN_PROGRESS' | 'SUCCESS' | 'FAILED' | 'IN_REVIEW' | 'PENDING_INQUIRY';
type CurrentState = 'SCREENING' | 'TITLE_FETCH' | 'BALANCE_CHECK' | 'FT_POST' | 'RAAST_TITLE_FETCH' | 'RAAST_POST' | 'ONELINK_TITLE_FETCH' | 'ONELINK_POST' | 'RTGS_BALANCE_CHECK' | 'RTGS_POST' | 'SUCCESS';
type StateStatus = 'PASS' | 'FAIL' | 'PENDING' | 'REVIEW';
interface StateHistoryEntry {
  stateCode: CurrentState;
  status: StateStatus;
  timestamp: string;
  failReasonCode?: string;
  primaryRef?: string;
  requestSnippet?: string;
  responseSnippet?: string;
  durationMs?: number;
  attempts?: number;
}
export interface Transaction {
  id: string;
  mtoName: string;
  mtoRef: string;
  bankRef: string;
  branchCode: string;
  agentCode: string;
  payoutType: PayoutType;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  corridor: string;
  purposeCode: string;
  purposeText: string;
  currentState: CurrentState;
  lifecycleStatus: LifecycleStatus;
  createdAt: string;
  updatedAt: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  railRefs: {
    RRN?: string;
    STAN?: string;
    RaastMsgId?: string;
    UETR?: string;
  };
  beneficiaryBankName?: string;
  ePRCNo?: string;
  ePRCDate?: string;
  stateHistory: StateHistoryEntry[];
  amendAllowed?: boolean;
  cancelAllowed?: boolean;
  amendStatus?: string;
  cancelStatus?: string;
}
const MTO_NAMES = ['RIA Money Transfer', 'InstantCash', 'MoneyGram', 'Western Union', 'Xpress Money'];
const AGENT_CODES = ['Bank of AJ&K', 'BIPL', 'FBL', 'HBL', 'UBL', 'MCB', 'ABL'];
const BRANCH_CODES = ['0001', '0002', '0003', '0004', '0005', '0006', '0007', '0008'];
const MOCK_TRANSACTIONS: Transaction[] = Array(50).fill(null).map((_, index) => {
  // Distribute transaction types: OTC, FT, IBFT
  let payoutType: PayoutType;
  if (index % 3 === 0) {
    payoutType = 'OTC';
  } else if (index % 3 === 1) {
    payoutType = 'FT';
  } else {
    payoutType = 'IBFT';
  }
  const lifecycleOptions: LifecycleStatus[] = ['IN_PROGRESS', 'SUCCESS', 'FAILED', 'IN_REVIEW', 'PENDING_INQUIRY'];
  const lifecycle = lifecycleOptions[index % 5];
  let currentState: CurrentState;
  let stateHistory: StateHistoryEntry[] = [];
  const baseTime = Date.now() - index * 3600000;
  // Determine amendAllowed based on state and lifecycle
  let amendAllowed = false;
  let cancelAllowed = false;
  // Build state history based on lifecycle
  if (lifecycle === 'SUCCESS') {
    currentState = 'SUCCESS';
    amendAllowed = false;
    cancelAllowed = false;
    if (payoutType === 'FT') {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 4000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 3000).toISOString(),
        primaryRef: `TF-${index}`,
        requestSnippet: `{"accountNo":"${String(index).padStart(16, '0')}"}`,
        responseSnippet: `{"title":"${String(index).padStart(16, '0')}","status":"ACTIVE"}`,
        durationMs: 800,
        attempts: 1
      }, {
        stateCode: 'BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `BC-${index}`,
        durationMs: 500,
        attempts: 1
      }, {
        stateCode: 'FT_POST',
        status: 'PASS',
        timestamp: new Date(baseTime - 1000).toISOString(),
        primaryRef: `RRN${String(index).padStart(12, '0')}`,
        requestSnippet: `{"amount":"${(500 + index * 10) * 279}","currency":"PKR"}`,
        responseSnippet: `{"rrn":"RRN${String(index).padStart(12, '0')}","stan":"${String(index).padStart(6, '0')}"}`,
        durationMs: 2300,
        attempts: 1
      }, {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `EPRC-${index}`,
        durationMs: 100,
        attempts: 1
      }];
    } else {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 4000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 3000).toISOString(),
        primaryRef: `RAAST-TF-${index}`,
        requestSnippet: `{"iban":"PK${String(index).padStart(22, '0')}"}`,
        responseSnippet: `{"title":"Beneficiary ${index + 1}","status":"ACTIVE"}`,
        durationMs: 1500,
        attempts: 1
      }, {
        stateCode: 'RAAST_POST',
        status: 'PASS',
        timestamp: new Date(baseTime - 1000).toISOString(),
        primaryRef: `RAAST${index}`,
        requestSnippet: `{"amount":"${(500 + index * 10) * 279}","currency":"PKR"}`,
        responseSnippet: `{"raastMsgId":"RAAST${index}","uetr":"UETR-${index}"}`,
        durationMs: 3200,
        attempts: 1
      }, {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `EPRC-${index}`,
        durationMs: 100,
        attempts: 1
      }];
    }
  } else if (lifecycle === 'IN_REVIEW') {
    currentState = 'SCREENING';
    amendAllowed = true;
    cancelAllowed = true;
    stateHistory = [{
      stateCode: 'SCREENING',
      status: 'REVIEW',
      timestamp: new Date(baseTime).toISOString(),
      primaryRef: `SCR-${index}`,
      failReasonCode: 'MANUAL_REVIEW_REQUIRED',
      requestSnippet: `{"remitter":"Remitter ${index + 1}","beneficiary":"Beneficiary ${index + 1}"}`,
      durationMs: 1500,
      attempts: 1
    }];
  } else if (lifecycle === 'PENDING_INQUIRY') {
    currentState = payoutType === 'IBFT' ? 'RAAST_POST' : 'FT_POST';
    amendAllowed = false;
    cancelAllowed = true;
    if (payoutType === 'FT') {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 3000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `TF-${index}`,
        durationMs: 800,
        attempts: 1
      }, {
        stateCode: 'BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(baseTime - 1000).toISOString(),
        primaryRef: `BC-${index}`,
        durationMs: 500,
        attempts: 1
      }, {
        stateCode: 'FT_POST',
        status: 'PENDING',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `FT-${index}`,
        failReasonCode: 'TIMEOUT_INQUIRY_NEEDED',
        durationMs: 30000,
        attempts: 2
      }];
    } else {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 1000).toISOString(),
        primaryRef: `RAAST-TF-${index}`,
        durationMs: 1500,
        attempts: 1
      }, {
        stateCode: 'RAAST_POST',
        status: 'PENDING',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `RAAST-${index}`,
        failReasonCode: 'TIMEOUT_INQUIRY_NEEDED',
        durationMs: 30000,
        attempts: 2
      }];
    }
  } else if (lifecycle === 'FAILED') {
    const failStates: CurrentState[] = payoutType === 'FT' ? ['TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'] : ['RAAST_TITLE_FETCH', 'ONELINK_POST', 'RTGS_POST'];
    currentState = failStates[index % failStates.length];
    // Amend allowed for early stage failures
    amendAllowed = ['TITLE_FETCH', 'SCREENING', 'BALANCE_CHECK', 'RAAST_TITLE_FETCH', 'ONELINK_TITLE_FETCH'].includes(currentState);
    cancelAllowed = true;
    if (currentState === 'TITLE_FETCH') {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `TF-${index}`,
        failReasonCode: 'INVALID_ACCOUNT',
        requestSnippet: `{"accountNo":"${String(index).padStart(16, '0')}"}`,
        responseSnippet: `{"error":"ACCOUNT_NOT_FOUND"}`,
        durationMs: 1200,
        attempts: 3
      }];
    } else if (currentState === 'BALANCE_CHECK') {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 3000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `TF-${index}`,
        durationMs: 800,
        attempts: 1
      }, {
        stateCode: 'BALANCE_CHECK',
        status: 'FAIL',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `BC-${index}`,
        failReasonCode: 'INSUFFICIENT_BALANCE',
        durationMs: 500,
        attempts: 1
      }];
    } else {
      stateHistory = [{
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(baseTime - 2000).toISOString(),
        primaryRef: `SCR-${index}`,
        durationMs: 1200,
        attempts: 1
      }, {
        stateCode: currentState,
        status: 'FAIL',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `POST-${index}`,
        failReasonCode: 'POSTING_ERROR',
        durationMs: 3000,
        attempts: 3
      }];
    }
  } else {
    // IN_PROGRESS
    const progressStates: CurrentState[] = payoutType === 'FT' ? ['SCREENING', 'TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'] : ['SCREENING', 'RAAST_TITLE_FETCH', 'RAAST_POST', 'ONELINK_TITLE_FETCH'];
    currentState = progressStates[index % progressStates.length];
    // Amend allowed for early stage in progress
    amendAllowed = ['SCREENING', 'TITLE_FETCH', 'BALANCE_CHECK', 'RAAST_TITLE_FETCH', 'ONELINK_TITLE_FETCH'].includes(currentState);
    cancelAllowed = true;
    stateHistory = [{
      stateCode: 'SCREENING',
      status: currentState === 'SCREENING' ? 'PENDING' : 'PASS',
      timestamp: new Date(baseTime - 2000).toISOString(),
      primaryRef: `SCR-${index}`,
      durationMs: currentState === 'SCREENING' ? undefined : 1200,
      attempts: 1
    }];
    if (currentState !== 'SCREENING') {
      stateHistory.push({
        stateCode: currentState,
        status: 'PENDING',
        timestamp: new Date(baseTime).toISOString(),
        primaryRef: `${currentState}-${index}`,
        attempts: 1
      });
    }
  }
  return {
    id: `TXN-${10000 + index}`,
    mtoName: index % 10 === 0 ? '' : VALID_MTOS[index % VALID_MTOS.length],
    mtoRef: `MTO-${20000 + index}`,
    bankRef: `BNK-${30000 + index}`,
    // Only assign branch code to OTC transactions
    branchCode: payoutType === 'OTC' ? BRANCH_CODES[index % BRANCH_CODES.length] : '',
    // Only assign agent code to OTC transactions - use validated sub-agents
    agentCode: payoutType === 'OTC' ? VALID_SUB_AGENTS[index % VALID_SUB_AGENTS.length] : '',
    payoutType,
    amountFCY: `${(500 + index * 10).toFixed(2)}`,
    currencyFCY: 'USD',
    amountPKR: `${((500 + index * 10) * 279).toLocaleString()}`,
    corridor: ['PK-AE', 'PK-SA', 'PK-UK', 'PK-US'][index % 4],
    purposeCode: ['FAM', 'SAL', 'EDU', 'MED'][index % 4],
    purposeText: ['Family Support', 'Salary', 'Education', 'Medical'][index % 4],
    currentState,
    lifecycleStatus: lifecycle,
    createdAt: new Date(baseTime - 5000).toISOString(),
    updatedAt: new Date(baseTime).toISOString(),
    remitterName: `Remitter ${index + 1}`,
    beneficiaryName: `Beneficiary ${index + 1}`,
    beneficiaryIBAN: `PK${String(index).padStart(22, '0')}`,
    beneficiaryBankName: payoutType === 'IBFT' ? ['Meezan Bank', 'HBL', 'UBL', 'Allied Bank', 'MCB Bank', 'Bank Alfalah', 'Faysal Bank', 'Askari Bank'][index % 8] : undefined,
    railRefs: {
      RRN: lifecycle === 'SUCCESS' ? `RRN${String(index).padStart(12, '0')}` : undefined,
      STAN: lifecycle === 'SUCCESS' ? `${String(index).padStart(6, '0')}` : undefined,
      RaastMsgId: payoutType === 'IBFT' && lifecycle === 'SUCCESS' ? `RAAST${index}` : undefined,
      UETR: payoutType === 'IBFT' && lifecycle === 'SUCCESS' ? `UETR-${index}` : undefined
    },
    ePRCNo: lifecycle === 'SUCCESS' ? `EPRC-${index}` : undefined,
    ePRCDate: lifecycle === 'SUCCESS' ? new Date(baseTime).toISOString() : undefined,
    stateHistory,
    amendAllowed,
    cancelAllowed,
    amendStatus: 'NONE',
    cancelStatus: 'NONE'
  } as any;
});
interface TransactionsTableProps {
  searchQuery: string;
}
const TransactionsTable: React.FC<TransactionsTableProps> = ({
  searchQuery
}) => {
  const {
    showToast
  } = useToast();
  const [filters, setFilters] = useState({
    search: searchQuery || '',
    payoutType: [] as PayoutType[],
    lifecycle: [] as LifecycleStatus[],
    currentState: [] as CurrentState[],
    corridor: [] as string[],
    purposeCode: [] as string[],
    agentCode: [] as string[],
    dateFrom: '',
    dateTo: '',
    amountFcyMin: '',
    amountFcyMax: '',
    amountPkrMin: '',
    amountPkrMax: '',
    mtoRef: '',
    txnId: '',
    branchCode: ''
  });
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);
  const [transactionError, setTransactionError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>(MOCK_TRANSACTIONS);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const [rowActions, setRowActions] = useState<RowAction[]>([]);
  const [viewTransaction, setViewTransaction] = useState(false);
  const [repushedTransactions, setRepushedTransactions] = useState<Transaction[]>([]);
  const [amendmentUpdates, setAmendmentUpdates] = useState(0);
  // Subscribe to store changes (repushed transactions + amendment updates)
  useEffect(() => {
    const updateFromStore = () => {
      const repushed = transactionStore.getRepushedTransactions();
      setRepushedTransactions(repushed as Transaction[]);
      // Trigger re-render to pick up amendment status changes
      setAmendmentUpdates(prev => prev + 1);
    };
    updateFromStore();
    const unsubscribe = transactionStore.subscribe(updateFromStore);
    return unsubscribe;
  }, []);
  // Add demo transactions to the list - Priority DEMO transactions first for visibility
  // Reorder: 015, 016, 019, 017, 018, 014 first, then checker inbox txns, then remaining demos, then mock
  const priorityDemoIds = ['TXN-DEMO-015', 'TXN-DEMO-016', 'TXN-DEMO-019', 'TXN-DEMO-017', 'TXN-DEMO-018', 'TXN-DEMO-014', 'TXN-30000', 'TXN-30001', 'TXN-30002', 'TXN-30003', 'TXN-30004'];
  const priorityDemos = priorityDemoIds.map(id => (DEMO_TRANSACTIONS as any[]).find(t => t.id === id)).filter(Boolean);
  const remainingDemos = (DEMO_TRANSACTIONS as any[]).filter(t => !priorityDemoIds.includes(t.id));
  // Apply amendment status, RTGS overrides, AND transaction overrides from the store
  const applyAmendmentStatus = (txn: any) => {
    // Check for RTGS override first (moved to RTGS / manually settled / reversed)
    const rtgsOverride = transactionStore.getRTGSOverride(txn.id);
    if (rtgsOverride) {
      return {
        ...txn,
        lifecycleStatus: rtgsOverride.lifecycleStatus,
        currentState: rtgsOverride.currentState,
        updatedAt: rtgsOverride.updatedAt,
        // Pass RTGS comment as manualActionComment so it shows in the details drawer
        ...(rtgsOverride.comment && {
          manualActionComment: {
            actionType: rtgsOverride.actionType || 'RTGS_ACTION',
            comment: rtgsOverride.comment,
            timestamp: rtgsOverride.updatedAt
          }
        })
      };
    }
    // Check for amendment override
    const override = transactionStore.getTransactionOverride(txn.id);
    if (override) {
      return {
        ...txn,
        amendStatus: override.amendStatus,
        ...(override.lifecycleStatus && {
          lifecycleStatus: override.lifecycleStatus
        }),
        ...(override.currentState && {
          currentState: override.currentState
        }),
        ...(override.beneficiaryIBAN && {
          beneficiaryIBAN: override.beneficiaryIBAN
        }),
        ...(override.beneficiaryName && {
          beneficiaryName: override.beneficiaryName
        }),
        ...(override.updatedAt && {
          updatedAt: override.updatedAt
        }),
        ...(override.amendmentHistory && {
          amendmentHistory: override.amendmentHistory
        })
      };
    }
    // Also check for simple amendment status (for amendments submitted but not yet acted on)
    const storeStatus = transactionStore.getAmendmentStatusForTransaction(txn.id);
    if (storeStatus !== 'NONE') {
      return {
        ...txn,
        amendStatus: storeStatus
      };
    }
    return txn;
  };
  // Collect all transaction IDs to deduplicate
  const seenIds = new Set<string>();
  const dedup = (txns: any[]) => {
    return txns.filter(txn => {
      if (seenIds.has(txn.id)) return false;
      seenIds.add(txn.id);
      return true;
    });
  };
  // Build allTransactions: priority demos first, then remaining demos, then failed txns, then repushed, then mock
  const allTransactions = [...dedup(priorityDemos.map(applyAmendmentStatus)), ...dedup(remainingDemos.map(applyAmendmentStatus)), ...dedup(MOCK_FAILED_TRANSACTIONS.map(applyAmendmentStatus)), ...dedup(repushedTransactions.map(applyAmendmentStatus)), ...dedup(MOCK_TRANSACTIONS.map(applyAmendmentStatus))];
  // Define row actions - View Details only (Amend removed - only available on Failed Transactions screen)
  const transactionRowActions: RowAction<Transaction>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewTransaction(row)
  }];
  // Apply filters whenever filters change
  useEffect(() => {
    let filtered = allTransactions;
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(txn => txn.id.toLowerCase().includes(searchLower) || txn.mtoName.toLowerCase().includes(searchLower) || txn.mtoRef.toLowerCase().includes(searchLower) || txn.bankRef.toLowerCase().includes(searchLower) || txn.beneficiaryName.toLowerCase().includes(searchLower) || txn.remitterName.toLowerCase().includes(searchLower) || txn.branchCode.toLowerCase().includes(searchLower) || txn.agentCode.toLowerCase().includes(searchLower));
    }
    // Txn ID filter
    if (filters.txnId) {
      filtered = filtered.filter(txn => txn.id.toLowerCase().includes(filters.txnId.toLowerCase()));
    }
    // Branch Code filter
    if (filters.branchCode) {
      filtered = filtered.filter(txn => txn.branchCode.toLowerCase().includes(filters.branchCode.toLowerCase()));
    }
    // Payout Type filter
    if (filters.payoutType.length > 0) {
      filtered = filtered.filter(txn => filters.payoutType.includes(txn.payoutType));
    }
    // Lifecycle Status filter
    if (filters.lifecycle.length > 0) {
      filtered = filtered.filter(txn => filters.lifecycle.includes(txn.lifecycleStatus));
    }
    // Current State filter
    if (filters.currentState.length > 0) {
      filtered = filtered.filter(txn => filters.currentState.includes(txn.currentState));
    }
    // Corridor filter
    if (filters.corridor.length > 0) {
      filtered = filtered.filter(txn => filters.corridor.includes(txn.corridor));
    }
    // Purpose Code filter
    if (filters.purposeCode.length > 0) {
      filtered = filtered.filter(txn => filters.purposeCode.includes(txn.purposeCode));
    }
    // Agent Code filter
    if (filters.agentCode.length > 0) {
      filtered = filtered.filter(txn => filters.agentCode.includes(txn.agentCode));
    }
    // Amount FCY filter
    if (filters.amountFcyMin || filters.amountFcyMax) {
      filtered = filtered.filter(txn => {
        const amount = parseFloat(txn.amountFCY);
        if (filters.amountFcyMin && amount < parseFloat(filters.amountFcyMin)) return false;
        if (filters.amountFcyMax && amount > parseFloat(filters.amountFcyMax)) return false;
        return true;
      });
    }
    // Amount PKR filter
    if (filters.amountPkrMin || filters.amountPkrMax) {
      filtered = filtered.filter(txn => {
        const amount = parseFloat(txn.amountPKR.replace(/,/g, ''));
        if (filters.amountPkrMin && amount < parseFloat(filters.amountPkrMin)) return false;
        if (filters.amountPkrMax && amount > parseFloat(filters.amountPkrMax)) return false;
        return true;
      });
    }
    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(txn => {
        const txnDate = new Date(txn.createdAt);
        return txnDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(txn => {
        const txnDate = new Date(txn.createdAt);
        return txnDate <= toDate;
      });
    }
    setFilteredTransactions(filtered);
    // Update filter chips
    const chips: FilterChip[] = [];
    if (filters.txnId) {
      chips.push({
        id: 'txnId',
        label: 'Txn ID',
        value: filters.txnId,
        onDelete: () => setFilters({
          ...filters,
          txnId: ''
        })
      });
    }
    if (filters.branchCode) {
      chips.push({
        id: 'branchCode',
        label: 'Branch Code',
        value: filters.branchCode,
        onDelete: () => setFilters({
          ...filters,
          branchCode: ''
        })
      });
    }
    if (filters.mtoRef) {
      chips.push({
        id: 'mtoRef',
        label: 'MTO',
        value: filters.mtoRef,
        onDelete: () => setFilters({
          ...filters,
          mtoRef: ''
        })
      });
    }
    if (filters.payoutType.length > 0) {
      chips.push({
        id: 'payoutType',
        label: 'Type',
        value: filters.payoutType.join(', '),
        onDelete: () => setFilters({
          ...filters,
          payoutType: []
        })
      });
    }
    if (filters.lifecycle.length > 0) {
      chips.push({
        id: 'lifecycle',
        label: 'Status',
        value: filters.lifecycle.map(s => s.replace(/_/g, ' ')).join(', '),
        onDelete: () => setFilters({
          ...filters,
          lifecycle: []
        })
      });
    }
    if (filters.currentState.length > 0) {
      chips.push({
        id: 'currentState',
        label: 'State',
        value: filters.currentState.map(s => s.replace(/_/g, ' ')).join(', '),
        onDelete: () => setFilters({
          ...filters,
          currentState: []
        })
      });
    }
    if (filters.amountFcyMin || filters.amountFcyMax) {
      chips.push({
        id: 'amountFcy',
        label: 'Amount FCY',
        value: filters.amountFcyMin && filters.amountFcyMax ? `${filters.amountFcyMin} - ${filters.amountFcyMax}` : filters.amountFcyMin ? `≥ ${filters.amountFcyMin}` : `≤ ${filters.amountFcyMax}`,
        onDelete: () => setFilters({
          ...filters,
          amountFcyMin: '',
          amountFcyMax: ''
        })
      });
    }
    if (filters.amountPkrMin || filters.amountPkrMax) {
      chips.push({
        id: 'amountPkr',
        label: 'Amount PKR',
        value: filters.amountPkrMin && filters.amountPkrMax ? `${filters.amountPkrMin} - ${filters.amountPkrMax}` : filters.amountPkrMin ? `≥ ${filters.amountPkrMin}` : `≤ ${filters.amountPkrMax}`,
        onDelete: () => setFilters({
          ...filters,
          amountPkrMin: '',
          amountPkrMax: ''
        })
      });
    }
    if (filters.dateFrom) {
      chips.push({
        id: 'dateFrom',
        label: 'From',
        value: new Date(filters.dateFrom).toLocaleDateString(),
        onDelete: () => setFilters({
          ...filters,
          dateFrom: ''
        })
      });
    }
    if (filters.dateTo) {
      chips.push({
        id: 'dateTo',
        label: 'To',
        value: new Date(filters.dateTo).toLocaleDateString(),
        onDelete: () => setFilters({
          ...filters,
          dateTo: ''
        })
      });
    }
    if (filters.agentCode.length > 0) {
      chips.push({
        id: 'agentCode',
        label: 'Agent Code',
        value: filters.agentCode.join(', '),
        onDelete: () => setFilters({
          ...filters,
          agentCode: []
        })
      });
    }
    setFilterChips(chips);
  }, [filters, repushedTransactions, amendmentUpdates]);
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value
    }));
  };
  const handleClearFilters = () => {
    setFilters({
      search: '',
      payoutType: [],
      lifecycle: [],
      currentState: [],
      corridor: [],
      purposeCode: [],
      agentCode: [],
      dateFrom: '',
      dateTo: '',
      amountFcyMin: '',
      amountFcyMax: '',
      amountPkrMin: '',
      amountPkrMax: '',
      mtoRef: '',
      txnId: '',
      branchCode: ''
    });
    setFilterChips([]);
    setFilteredTransactions(MOCK_TRANSACTIONS);
  };
  const handleExport = () => {
    // Export logic here
  };
  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDrawerOpen(true);
  };
  const confirmRetry = async () => {
    if (!selectedTransaction) return;
    setIsRetrying(true);
    setRetryError(null);
    try {
      // Retry logic here
      await new Promise(resolve => setTimeout(resolve, 2000));
      setRetryDialogOpen(false);
      setTransactionError(null);
      setIsLoadingTransaction(false);
    } catch (error) {
      setRetryError('Failed to retry transaction');
      setIsRetrying(false);
    }
  };
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    // Check if both dates are selected and validate
    if (newFilters.dateFrom && newFilters.dateTo) {
      if (!validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
        setDateRangeError('❌ Date range cannot exceed 3 months');
        return;
      }
    }
    // Clear error if validation passes
    setDateRangeError('');
    setFilters(newFilters);
  };
  const columns: Column<Transaction>[] = [{
    id: 'id',
    label: 'Txn ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-2735" data-mp="g0fzbkhajtqk">
          {row.id}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.txnId} onChange={e => setFilters({
      ...filters,
      txnId: e.target.value
    })} sx={{
      width: '100%',
      minWidth: 120
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2737" data-mp="pwj6j4r02ikd">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2738" data-mp="35p0038b9dk7l" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-2736" data-mp="6aegb73sp1ii" />
  }, {
    id: 'agentCode',
    label: 'Agent Code',
    renderCell: row => <Chip label={row.agentCode || ''} size="small" sx={{
      backgroundColor: 'rgba(79, 70, 229, 0.1)',
      color: '#4F46E5',
      fontWeight: 600,
      fontSize: '0.7rem',
      visibility: row.agentCode ? 'visible' : 'hidden'
    }} data-id="element-2739" data-mp="1b8i0qz3atg4y" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-2740" data-mp="xf0hzgifs7fz">
          <Select multiple value={filters.agentCode} onChange={e => setFilters({
        ...filters,
        agentCode: e.target.value as string[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.join(', ')} data-id="element-2741" data-mp="sh8oii258d9o">
            {VALID_SUB_AGENTS.map(code => <MenuItem key={code} value={code} data-id="element-2742" data-mp="3vr5bw65exngc">
                {code}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderCell: row => <Chip label={row.branchCode || ''} size="small" sx={{
      backgroundColor: 'rgba(6, 182, 212, 0.1)',
      color: '#06B6D4',
      fontWeight: 600,
      fontSize: '0.7rem',
      visibility: row.branchCode ? 'visible' : 'hidden'
    }} data-id="element-2743" data-mp="de78fp9ewchf" />,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.branchCode} onChange={e => setFilters({
      ...filters,
      branchCode: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2745" data-mp="14n0pgpm9rdcx">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2746" data-mp="zo78uobor68j" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-2744" data-mp="3qu5a47flj6v1" />
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Box sx={{
      minWidth: 150
    }} data-id="element-2747" data-mp="1nkl4z6358zkv">
          {row.mtoName ? <Typography variant="body2" fontWeight={500} noWrap data-id="element-2748" data-mp="1ilqoiasnfn3x">
              {row.mtoName}
            </Typography> : <Chip label="No MTO" size="small" sx={{
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444',
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2749" data-mp="1xditbsuthire" />}
        </Box>,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-2750" data-mp="2kyjkm01o8zrt">
          <Select value={filters.mtoRef || 'all'} onChange={e => setFilters({
        ...filters,
        mtoRef: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-2751" data-mp="2kwqdykr26fjt">
            <MenuItem value="all" data-id="element-2752" data-mp="226qwok1gm4qy">All</MenuItem>
            {VALID_MTOS.map(name => <MenuItem key={name} value={name} data-id="element-2753" data-mp="26ztq8s68le75">
                {name}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : row.payoutType === 'IBFT' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2754" data-mp="1j9h86oha212u" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-2755" data-mp="3c6uw1d5z8a2q">
          <Select multiple value={filters.payoutType} onChange={e => setFilters({
        ...filters,
        payoutType: e.target.value as PayoutType[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.join(', ')} data-id="element-2756" data-mp="3c860lf78xluw">
            <MenuItem value="FT" data-id="element-2757" data-mp="2oi1wl6q5bk3d">FT</MenuItem>
            <MenuItem value="IBFT" data-id="element-2758" data-mp="2jh4hx44ch86m">IBFT</MenuItem>
            <MenuItem value="OTC" data-id="element-2759" data-mp="2jgcmbtaqzagc">OTC</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'beneficiaryBankName',
    label: 'Beneficiary Bank',
    renderCell: row => row.payoutType === 'IBFT' && row.beneficiaryBankName ? <Chip label={row.beneficiaryBankName} size="small" sx={{
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#059669',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2760" data-mp="17z8lmfbgp5aj" /> : <Typography variant="body2" sx={{
      color: '#CBD5E1',
      fontSize: '0.75rem'
    }} data-id="element-2761" data-mp="1mo4uufp98ocq">
            —
          </Typography>
  }, {
    id: 'amountFCY',
    label: 'Amount FCY',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      minWidth: 100
    }} data-id="element-2762" data-mp="3tgp1am2ruz70">
          {row.currencyFCY} {row.amountFCY}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-2763" data-mp="39lx8xhrjm0kp">
          <TextField size="small" placeholder="Min" value={filters.amountFcyMin} onChange={e => setFilters({
        ...filters,
        amountFcyMin: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-2764" data-mp="3nc94jqnc6hgl" />
          <Icon icon="lucide:arrow-right" width={16} height={16} data-id="element-2765" data-mp="dglb7s71s95v" />
          <TextField size="small" placeholder="Max" value={filters.amountFcyMax} onChange={e => setFilters({
        ...filters,
        amountFcyMax: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-2766" data-mp="difyhd5skdd4" />
        </Box>
  }, {
    id: 'amountPKR',
    label: 'Amount PKR',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      minWidth: 120
    }} data-id="element-2767" data-mp="2okif1ec5dk87">
          PKR {row.amountPKR}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-2768" data-mp="32jrj5fn2wnvt">
          <TextField size="small" placeholder="Min" value={filters.amountPkrMin} onChange={e => setFilters({
        ...filters,
        amountPkrMin: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-2769" data-mp="37ht3e3w8qt8v" />
          <Icon icon="lucide:arrow-right" width={16} height={16} data-id="element-2770" data-mp="3vefxgdtbc79h" />
          <TextField size="small" placeholder="Max" value={filters.amountPkrMax} onChange={e => setFilters({
        ...filters,
        amountPkrMax: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-2771" data-mp="3vg959wfnwxj2" />
        </Box>
  }, {
    id: 'currentState',
    label: 'Current State',
    renderCell: row => <Chip label={row.currentState.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: 'rgba(100, 116, 139, 0.1)',
      color: '#64748B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2772" data-mp="g9mrrq120dcy" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-2773" data-mp="8okayfa41qeu">
          <Select multiple value={filters.currentState} onChange={e => setFilters({
        ...filters,
        currentState: e.target.value as CurrentState[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.map(s => s.replace(/_/g, ' ')).join(', ')} data-id="element-2774" data-mp="3qr89w80nejb">
            <MenuItem value="SCREENING" data-id="element-2775" data-mp="3789cltoky3ez">SCREENING</MenuItem>
            <MenuItem value="TITLE_FETCH" data-id="element-2776" data-mp="377rbojqz310q">TITLE FETCH</MenuItem>
            <MenuItem value="BALANCE_CHECK" data-id="element-2777" data-mp="336fftwhi9jk3">BALANCE CHECK</MenuItem>
            <MenuItem value="FT_POST" data-id="element-2778" data-mp="3rxbo9a2h5jmd">FT POST</MenuItem>
            <MenuItem value="RAAST_TITLE_FETCH" data-id="element-2779" data-mp="3rymudgje73gn">RAAST TITLE FETCH</MenuItem>
            <MenuItem value="RAAST_POST" data-id="element-2780" data-mp="raagtr4kbx1">RAAST POST</MenuItem>
            <MenuItem value="SUCCESS" data-id="element-2781" data-mp="sv6knt382x0">SUCCESS</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'lifecycleStatus',
    label: 'Lifecycle',
    renderCell: row => {
      const colors: Record<string, {
        bg: string;
        color: string;
      }> = {
        IN_PROGRESS: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        SUCCESS: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        },
        FAILED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        },
        IN_REVIEW: {
          bg: 'rgba(245, 158, 11, 0.1)',
          color: '#F59E0B'
        },
        PENDING_INQUIRY: {
          bg: 'rgba(139, 92, 246, 0.1)',
          color: '#8B5CF6'
        }
      };
      const style = colors[row.lifecycleStatus];
      return <Chip label={row.lifecycleStatus.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2782" data-mp="2iyeeck9vri4x" />;
    },
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-2783" data-mp="36o8ouv6q8um0">
          <Select multiple value={filters.lifecycle} onChange={e => setFilters({
        ...filters,
        lifecycle: e.target.value as LifecycleStatus[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.map(s => s.replace(/_/g, ' ')).join(', ')} data-id="element-2784" data-mp="31o49x9lgw7xw">
            <MenuItem value="IN_PROGRESS" data-id="element-2785" data-mp="3lb2hrqvrfl4l">IN PROGRESS</MenuItem>
            <MenuItem value="SUCCESS" data-id="element-2786" data-mp="3lbklt9onz3n2">SUCCESS</MenuItem>
            <MenuItem value="FAILED" data-id="element-2787" data-mp="3qa5mtbbqprck">FAILED</MenuItem>
            <MenuItem value="IN_REVIEW" data-id="element-2788" data-mp="3qci03b6jzdja">IN REVIEW</MenuItem>
            <MenuItem value="PENDING_INQUIRY" data-id="element-2789" data-mp="3ud22at5zh7ca">PENDING INQUIRY</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'transactionStatus',
    label: 'Transaction Status',
    renderCell: row => {
      // Derive transaction status from lifecycle
      let transactionStatus = 'PENDING';
      if (row.lifecycleStatus === 'SUCCESS') {
        transactionStatus = 'FUNDS_TRANSFERRED';
      } else if (row.lifecycleStatus === 'FAILED') {
        transactionStatus = 'FAILED';
      } else if (row.lifecycleStatus === 'IN_REVIEW') {
        transactionStatus = 'UNDER_REVIEW';
      }
      return <Chip label={transactionStatus.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B',
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2790" data-mp="qsxjsd80x4pf" />;
    }
  }, {
    id: 'systemStatus',
    label: 'Status',
    renderCell: row => {
      // Derive system status from current state and lifecycle
      let systemStatus = 'IN_PROGRESS';
      if (row.lifecycleStatus === 'SUCCESS') {
        systemStatus = 'COMPLETED';
      } else if (row.lifecycleStatus === 'FAILED') {
        systemStatus = 'FAILED';
      } else if (row.currentState === 'SCREENING') {
        systemStatus = 'IN_PROGRESS (Screening)';
      }
      const colors = {
        IN_PROGRESS: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        'IN_PROGRESS (Screening)': {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        COMPLETED: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        },
        FAILED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        }
      };
      const style = colors[systemStatus as keyof typeof colors] || colors['IN_PROGRESS'];
      return <Chip label={systemStatus} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2791" data-mp="303zuezleou88" />;
    }
  }, {
    id: 'createdAt',
    label: 'Created',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-2792" data-mp="2v33ibyqwi1rq">
          {new Date(row.createdAt).toLocaleString()}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }} data-id="element-2793" data-mp="1c578cerlxcyw">
          <Box sx={{
        display: 'flex',
        gap: 0.5
      }} data-id="element-2794" data-mp="1798fadvc36s5">
            <TextField size="small" type="date" value={filters.dateFrom} onChange={e => handleDateChange('dateFrom', e.target.value)} error={!!dateRangeError} inputProps={{
          max: filters.dateTo ? new Date(new Date(filters.dateTo).setMonth(new Date(filters.dateTo).getMonth() - 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-2795" data-mp="1h5br6bfzmjsa" />
            <TextField size="small" type="date" value={filters.dateTo} onChange={e => handleDateChange('dateTo', e.target.value)} error={!!dateRangeError} inputProps={{
          min: filters.dateFrom ? new Date(new Date(filters.dateFrom).setMonth(new Date(filters.dateFrom).getMonth() + 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-2796" data-mp="2rlyzrc0e81kb" />
          </Box>
          {dateRangeError && <Typography variant="caption" color="error" sx={{
        fontSize: '0.7rem',
        mt: 0.5
      }} data-id="element-2797" data-mp="edfz7ts6rv53">
              {dateRangeError}
            </Typography>}
        </Box>
  }, {
    id: 'updatedAt',
    label: 'Last Update',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-2798" data-mp="x4ff85hpdum8">
          {new Date(row.updatedAt).toLocaleString()}
        </Typography>,
    renderFilter: () => <Box sx={{
      height: 36
    }} data-id="element-2799" data-mp="33x9k8nubzkxt" />
  }];
  return <>
      <DataTable title="Transactions" data={filteredTransactions} columns={columns} rowActions={transactionRowActions} keyField="id" filterChips={filterChips} onClearAllFilters={handleClearFilters} loading={loading} initialVisibleColumns={['id', 'agentCode', 'branchCode', 'mtoName', 'payoutType', 'beneficiaryBankName', 'amountFCY', 'amountPKR', 'currentState', 'lifecycleStatus', 'transactionStatus', 'systemStatus', 'createdAt']} onExport={handleExport} emptyStateMessage="No transactions found" onRowClick={row => {
      setSelectedTransaction(row);
      setDrawerOpen(true);
    }} data-id="element-2800" data-mp="38wm36xi307v4" />

      {selectedTransaction && <TransactionDetailsDrawer open={drawerOpen} onClose={() => {
      setDrawerOpen(false);
      setSelectedTransaction(null);
    }} transaction={selectedTransaction} isLoading={isLoadingTransaction} error={transactionError} onRetry={() => handleViewTransaction(selectedTransaction)} data-id="element-2801" data-mp="7bswateuzlxg" />}

      {selectedTransaction && <ConfirmDialog open={retryDialogOpen} title="Retry Transaction" message={`Are you sure you want to retry transaction ${selectedTransaction.id}?`} confirmLabel="Retry" showReason={true} reasonLabel="Reason for retry (optional)" reasonPlaceholder="Enter reason for retrying this transaction..." onConfirm={confirmRetry} onCancel={() => setRetryDialogOpen(false)} isLoading={isRetrying} error={retryError} data-id="element-2802" data-mp="bzsccy1kg6lh" />}
    </>;
};
export default TransactionsTable;